import { TRUST_SCORE_SERVICE_OFFERING_ID } from "@constants/verification-service-details-constants";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { formatScoreModifiers } from "@formatters/rp-services/rp-service-configuration-score-formatter";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationServiceOffering
} from "@idw-types/data/rp-service-configuration";
import { isAssertionScoreEnabled, isTrustScoreEnabled } from "@utils/scores/score-util";
import { RpServiceConfigurationServiceOfferingContainer } from "../rp-service-configuration-service-offering-container/rp-service-configuration-service-offering-container";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";

type RpServiceConfigurationServiceOfferingsContainerProps = {
    isDisabled: boolean;
    initialRpServiceConfiguration: FormattedRpServiceConfiguration;
    selectedRpServiceConfigurationServiceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    rpServiceConfigurationServiceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    assertionStatusMaps: Array<ApAssertionStatusMap>;
    assertionScoreServiceOfferingIDs: Array<string>;
    trustScoreServiceOfferingIDs: Array<string>;
    scoreServiceOfferingIDs: Array<string>;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const RpServiceConfigurationServiceOfferingsContainer = (props: RpServiceConfigurationServiceOfferingsContainerProps) => {
    const {
        isDisabled,
        initialRpServiceConfiguration,
        selectedRpServiceConfigurationServiceOfferings,
        rpServiceConfigurationServiceOfferings,
        assertionStatusMaps,
        assertionScoreServiceOfferingIDs,
        trustScoreServiceOfferingIDs,
        scoreServiceOfferingIDs,
        rpServiceConfigurationState,
        setRpServiceConfigurationState
    } = props;

    const setServiceOfferingDataSource = (rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering, newDataSource: string) => {
        const newServiceOfferings = rpServiceConfigurationState.serviceOfferings.map(so => {
            if (so.uuid === rpServiceConfigurationServiceOffering.uuid) {
                return {
                    ...so,
                    dataSource: newDataSource
                }
            } else {
                return so;
            }
        });

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            serviceOfferings: newServiceOfferings
        });
    };

    const addFallbackServiceOffering = (
        rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering,
        fallbackServiceOffering: FormattedRpServiceConfigurationServiceOffering,
        fallbackServiceOfferingDataSource: string
    ) => {
        const newServiceOfferings = rpServiceConfigurationState.serviceOfferings.map(so => {
            if (so.uuid === rpServiceConfigurationServiceOffering.uuid) {
                return {
                    ...so,
                    fallbackServiceOfferingID: fallbackServiceOffering.serviceOfferingID
                };
            } else if (so.uuid === fallbackServiceOffering.uuid) {
                return {
                    ...so,
                    fallbackStatus: "fallback",
                    dataSource: fallbackServiceOfferingDataSource,
                    status: EntityStatusTypes.ACTIVE,
                    addedToService: true
                };
            } else {
                return so;
            }
        });

        updateServiceOfferingsAndScores(newServiceOfferings);
    };

    const removeFallbackServiceOffering = (rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering, fallbackServiceOffering: FormattedRpServiceConfigurationServiceOffering) => {
        const newServiceOfferings = rpServiceConfigurationState.serviceOfferings.map(so => {
            if (so.uuid === rpServiceConfigurationServiceOffering.uuid) {
                if (rpServiceConfigurationServiceOffering.id) {
                    return {
                        ...so,
                        fallbackRPServiceSOID: null,
                        fallbackServiceOfferingID: null
                    };
                }
            } else if (so.uuid === fallbackServiceOffering.uuid) {
                if (fallbackServiceOffering.id) {
                    return {
                        ...fallbackServiceOffering,
                        fallbackStatus: null,
                        dataSource: null,
                        status: EntityStatusTypes.INACTIVE
                    };
                } else {
                    //it doesnt exist in the database so just reset it to the initial value
                    return initialRpServiceConfiguration.serviceOfferings.find(initialSO => initialSO.uuid === fallbackServiceOffering.uuid);
                }
            }
            return so;
        });

        updateServiceOfferingsAndScores(newServiceOfferings);
    };

    const removeServiceOffering = (rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering) => {

        let newServiceOfferings = rpServiceConfigurationState.serviceOfferings.map(so => {
            // if we find the service offering to remove
            if (so.uuid === rpServiceConfigurationServiceOffering.uuid) {
                // and it exists in the database, set it to inactive and clear out the fallback service
                if (rpServiceConfigurationServiceOffering.id) {
                    const assertions = rpServiceConfigurationServiceOffering.assertions.map(assertion => {
                        const addedToService = assertion.id ? assertion.addedToService : false;
                        return {
                            ...assertion,
                            addedToService,
                            status: EntityStatusTypes.INACTIVE
                        };
                    });
                    return {
                        ...rpServiceConfigurationServiceOffering,
                        assertions,
                        dataSource: null,
                        fallbackRPServiceSOID: null,
                        fallbackServiceOfferingID: null,
                        status: EntityStatusTypes.INACTIVE
                    };
                } else {
                    // it doesnt exist in the database, so just reset it to the initial value
                    return initialRpServiceConfiguration.serviceOfferings.find(initialSO => initialSO.uuid === rpServiceConfigurationServiceOffering.uuid);
                }
            } else if (
                so.fallbackStatus === "fallback" &&
                (so.id === rpServiceConfigurationServiceOffering.fallbackRPServiceSOID || so.serviceOfferingID === rpServiceConfigurationServiceOffering.fallbackServiceOfferingID)
            ) {
                // if we find the fallback service and it exists in the database, set it to inactive and clear out the primary service
                if (so.id) {
                    const assertions = so.assertions.map(assertion => {
                        const addedToService = assertion.id ? assertion.addedToService : false;
                        return {
                            ...assertion,
                            addedToService,
                            status: EntityStatusTypes.INACTIVE
                        };
                    });
                    return {
                        ...so,
                        assertions,
                        dataSource: null,
                        fallbackStatus: null,
                        status: EntityStatusTypes.INACTIVE
                    };
                } else {
                    //it doesnt exist in the database so just reset it to the initial value
                    return initialRpServiceConfiguration.serviceOfferings.find(initialSO => initialSO.uuid === so.uuid);
                }
            }
            return so;
        });


        newServiceOfferings = newServiceOfferings.map(so => {
            if (so.serviceOfferingID === TRUST_SCORE_SERVICE_OFFERING_ID) {
                // if the trust score service offering is active,
                //  we need to check to see if there are any service offerings active that the Trust Score SO needs
                // if not, we need to inactive the trust score service offering
                if (so.addedToService && so.status === EntityStatusTypes.ACTIVE) {
                    const shouldTrustScoreBeEnabled = newServiceOfferings.some(newSO =>
                        scoreServiceOfferingIDs.includes(newSO.serviceOfferingID) &&
                        newSO.addedToService &&
                        newSO.status === EntityStatusTypes.ACTIVE
                    );
                    return {
                        ...so,
                        status: !shouldTrustScoreBeEnabled ? EntityStatusTypes.INACTIVE : so.status,
                        addedToService: so.id === null ? false : so.addedToService
                    };
                }
            }
            return so;
        });

        updateServiceOfferingsAndScores(newServiceOfferings);
    };

    const updateServiceOfferingsAndScores = (newServiceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>) => {
        const newRpServiceConfigurationState = { ...rpServiceConfigurationState, serviceOfferings: newServiceOfferings };

        if (isAssertionScoreEnabled(newServiceOfferings, assertionScoreServiceOfferingIDs)) {
            newRpServiceConfigurationState.assertionScoreModifiers = formatScoreModifiers(rpServiceConfigurationState.assertionScoreModifiers, newServiceOfferings,
                assertionStatusMaps
            );
        }

        if (isTrustScoreEnabled(newServiceOfferings, trustScoreServiceOfferingIDs)) {
            newRpServiceConfigurationState.trustScoreModifiers = formatScoreModifiers(rpServiceConfigurationState.trustScoreModifiers, newServiceOfferings,
                assertionStatusMaps
            );
        }

        setRpServiceConfigurationState(newRpServiceConfigurationState);
    };

    return (
        <div>
            {selectedRpServiceConfigurationServiceOfferings.map(so => (
                <RpServiceConfigurationServiceOfferingContainer
                    key={`selected-service-offering-${so.serviceOfferingID}`}
                    isDisabled={isDisabled}
                    canModifyFallbackServiceOfferings={rpServiceConfigurationState.permissions.canModifyFallbackServiceOfferings}
                    canViewFallbackServiceOfferings={rpServiceConfigurationState.permissions.canViewFallbackServiceOfferings}
                    rpServiceConfigurationServiceOffering={so}
                    rpServiceConfigurationServiceOfferings={rpServiceConfigurationServiceOfferings}
                    removeServiceOffering={removeServiceOffering}
                    removeFallbackServiceOffering={removeFallbackServiceOffering}
                    addFallbackServiceOffering={addFallbackServiceOffering}
                    setServiceOfferingDataSource={setServiceOfferingDataSource}
                />
            ))}
        </div>
    );
};